import React, { useCallback, useMemo, useState } from 'react';
import {
  Box, Button, ButtonText, Center, Spinner, Text, VStack,
} from '@gluestack-ui/themed';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import {
  getGetScheduledChangeQueryKey,
  useGetScheduledChange,
  useListScheduledChanges,
  useSwapScheduledChangeQuestion,
} from '@/common/api/endpoints/scheduled-changes/scheduled-changes';
import type { OralExamQuestion, ScheduledQuestion } from '@/common/api/models';
import type { ReferringResource } from '@/common/api/models/referringResource';
import type { ScheduledChangeSwapRequestReasonCode } from '@/common/api/models/scheduledChangeSwapRequestReasonCode';
import { useDialog } from '@/common/context/DialogContext';
import type { LecturerRouteParamList } from '@/common/navigation/RoleNavigations/Lecturer/types';
import { SwapQuestionDialog } from './components/SwapQuestionDialog';
import { scheduledChangeDetailRefetchInterval } from './detailRefetchInterval';
import { ScheduledChangesList } from './ScheduledChangesList';
import { ScheduledChangesDetail } from './ScheduledChangesDetail';

type SwapDialogState = {
  questionId: string;
  topicLabel: string;
  currentQuestion: OralExamQuestion;
  alternatives: OralExamQuestion[];
  referringResources: ReferringResource[];
};

const ScheduledChangesScreen: React.FC = () => {
  const { t } = useTranslation();
  const { showToastMessage } = useDialog();
  const queryClient = useQueryClient();
  const navigation = useNavigation<StackNavigationProp<LecturerRouteParamList>>();
  const route = useRoute<RouteProp<LecturerRouteParamList, 'ScheduledChanges'>>();
  const changeId = route.params?.changeId;

  const listQuery = useListScheduledChanges();
  const detailQuery = useGetScheduledChange(changeId ?? '', {
    query: {
      enabled: !!changeId,
      refetchInterval: scheduledChangeDetailRefetchInterval,
    },
  });

  const [swapDialog, setSwapDialog] = useState<SwapDialogState | null>(null);
  const [selectedReplacementId, setSelectedReplacementId] = useState<string | undefined>();
  const [selectedReason, setSelectedReason] = useState<ScheduledChangeSwapRequestReasonCode | undefined>();
  const [swapNote, setSwapNote] = useState('');
  const [selectedResourceIds, setSelectedResourceIds] = useState<string[]>([]);
  const [swapError, setSwapError] = useState<string | undefined>();
  const [showConflictBanner, setShowConflictBanner] = useState(false);

  const swapMutation = useSwapScheduledChangeQuestion();

  const items = listQuery.data?.data?.data ?? [];
  const detail = detailQuery.data?.data?.data;

  const otherTopicLabel = t('lecturer.scheduledChanges.oralQuestions.otherTopic');

  const resetSwapForm = useCallback(() => {
    setSelectedReplacementId(undefined);
    setSelectedReason(undefined);
    setSwapNote('');
    setSwapError(undefined);
    setSelectedResourceIds([]);
  }, []);

  const closeSwapDialog = useCallback(() => {
    if (swapMutation.isPending) return;
    setSwapDialog(null);
    resetSwapForm();
  }, [resetSwapForm, swapMutation.isPending]);

  const openList = useCallback(() => {
    navigation.navigate('ScheduledChanges', {});
  }, [navigation]);

  const openDetail = useCallback((id: string) => {
    navigation.navigate('ScheduledChanges', { changeId: id });
  }, [navigation]);

  const openModuleVersion = useCallback((moduleId: string, version: string) => {
    navigation.navigate('ModuleEditor', { moduleId, version, taskId: null });
  }, [navigation]);

  const findQuestionRow = useCallback(
    (qid: string): ScheduledQuestion | undefined => detail?.questions?.find(
      (row) => row.question?.questionId === qid,
    ),
    [detail?.questions],
  );

  const handleSwapPress = useCallback((qid: string) => {
    const row = findQuestionRow(qid);
    if (!row?.question) return;
    resetSwapForm();
    setSwapDialog({
      questionId: qid,
      topicLabel: row.question.concept ?? otherTopicLabel,
      currentQuestion: row.question,
      alternatives: row.alternatives ?? [],
      referringResources: row.referringResources ?? [],
    });
  }, [findQuestionRow, otherTopicLabel, resetSwapForm]);

  const handleSelectReason = useCallback((code: ScheduledChangeSwapRequestReasonCode) => {
    setSelectedReason(code);
    if (code !== 'resource_not_exam_relevant') {
      setSelectedResourceIds([]);
    }
  }, []);

  const handleToggleResource = useCallback((resourceId: string, checked: boolean) => {
    setSelectedResourceIds((current) => (
      checked ? [...current, resourceId] : current.filter((id) => id !== resourceId)
    ));
  }, []);

  const handleSubmitSwap = useCallback(async () => {
    if (!changeId || !swapDialog || !detail?.etag || !selectedReplacementId || !selectedReason) {
      return;
    }
    setSwapError(undefined);
    try {
      const response = await swapMutation.mutateAsync({
        changeId,
        data: {
          questionId: swapDialog.questionId,
          replacementId: selectedReplacementId,
          reasonCode: selectedReason,
          note: swapNote.trim() || undefined,
          etag: detail.etag,
          toggleResourceIds: selectedResourceIds.length ? selectedResourceIds : undefined,
        },
      });
      const updated = response.data?.data;
      if (updated && changeId) {
        queryClient.setQueryData(getGetScheduledChangeQueryKey(changeId), response);
      }
      setSwapDialog(null);
      resetSwapForm();
      showToastMessage('success', t('lecturer.scheduledChanges.swap.success'));
    } catch (error) {
      const status = error instanceof AxiosError ? error.response?.status : undefined;
      if (status === 409) {
        setSwapDialog(null);
        resetSwapForm();
        setShowConflictBanner(true);
        await detailQuery.refetch();
        return;
      }
      const detailMessage = error instanceof AxiosError
        && typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail
        : undefined;
      setSwapError(detailMessage ?? t('lecturer.scheduledChanges.swap.error'));
    }
  }, [
    changeId,
    detail?.etag,
    detailQuery,
    queryClient,
    resetSwapForm,
    selectedReason,
    selectedReplacementId,
    showToastMessage,
    selectedResourceIds,
    swapDialog,
    swapMutation,
    swapNote,
    t,
  ]);

  const dialogProps = useMemo(() => swapDialog, [swapDialog]);

  if (listQuery.isLoading || (!!changeId && detailQuery.isLoading)) {
    return (
      <Center flex={1}>
        <Spinner size="large" />
      </Center>
    );
  }

  if (listQuery.isError) {
    return (
      <Center flex={1} px="$6">
        <VStack space="md" alignItems="center">
          <Text color="$textBody">
            {t('lecturer.scheduledChanges.loadError')}
          </Text>
          <Button variant="outline" action="secondary" onPress={() => listQuery.refetch()}>
            <ButtonText>{t('lecturer.scheduledChanges.retry')}</ButtonText>
          </Button>
        </VStack>
      </Center>
    );
  }

  if (!changeId) {
    if (!items.length) {
      return (
        <Center flex={1} px="$6">
          <VStack space="sm" alignItems="center">
            <Text size="lg" color="$textSubtle">
              {t('lecturer.scheduledChanges.emptyTitle')}
            </Text>
            <Text size="sm" color="$textHint" textAlign="center">
              {t('lecturer.scheduledChanges.emptyBody')}
            </Text>
          </VStack>
        </Center>
      );
    }
    return (
      <Box flex={1} bg="$bgApp">
        <ScheduledChangesList items={items} onSelect={openDetail} />
      </Box>
    );
  }

  if (detailQuery.isError || !detail) {
    return (
      <Center flex={1} px="$6">
        <VStack space="md" alignItems="center">
          <Text color="$textBody">
            {t('lecturer.scheduledChanges.notFound')}
          </Text>
          <Button variant="outline" action="secondary" onPress={openList}>
            <ButtonText>{t('lecturer.scheduledChanges.backToList')}</ButtonText>
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Box flex={1} bg="$bgApp">
      <ScheduledChangesDetail
        change={detail}
        showConflictBanner={showConflictBanner}
        onDismissConflict={() => setShowConflictBanner(false)}
        onBack={openList}
        onOpenVersion={openModuleVersion}
        onSwap={handleSwapPress}
      />
      {dialogProps ? (
        <SwapQuestionDialog
          isOpen
          topicLabel={dialogProps.topicLabel}
          currentQuestion={dialogProps.currentQuestion}
          alternatives={dialogProps.alternatives}
          selectedReplacementId={selectedReplacementId}
          referringResources={dialogProps.referringResources}
          selectedReason={selectedReason}
          selectedResourceIds={selectedResourceIds}
          note={swapNote}
          errorMessage={swapError}
          pending={swapMutation.isPending}
          onClose={closeSwapDialog}
          onSelectReplacement={setSelectedReplacementId}
          onSelectReason={handleSelectReason}
          onToggleResource={handleToggleResource}
          onNoteChange={setSwapNote}
          onSubmit={handleSubmitSwap}
        />
      ) : null}
    </Box>
  );
};

export default ScheduledChangesScreen;
